import {
    Box,
    Paper,
    Card,
    Typography,
    Grid,
    TextField,
    FormGroup,
    Stack,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Alert,
    Snackbar,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import pkg from '../../package.json';
import { ResultsHookPostNewArtist, USignWebHookResponse, UserArtPacks, snackMessage } from '../types';
import { FormEvent, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { HOOK_URL_ARTIST } from '../constants';
import { callHook } from '../services/usign';
import React from 'react';

const initialUser: UserArtPacks = {
    uId: '',
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    artistName: '',
    birthYear: '',
    birthCountry: '',
    deathYear: '',
    deathCountry: '',
    residenceCountry: '',
    mintWallet: '',
    salesWallet: '',
    portfolioLink: '',
    twitterHandle: '',
    oncyberGallery: '',
    salesTier: 'emerging',
    status: '',
};

const EditUser = () => {
    const [showError, setShowError] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState<snackMessage>({ severity: 'success', msg: '' });
    const [formData, setFormData] = useState<UserArtPacks>(initialUser);
    const [needSave, setNeedSave] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const handleCloseErrorMsg = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setShowError(false);
    };

    const changeFormData = (data: Partial<UserArtPacks>) => {
        setFormData((prev) => ({ ...prev, ...data }));
        setNeedSave(true);
    };

    const saveData = async (): Promise<boolean> => {
        const dataToSave = { ...formData };

        try {
            const answer = await callHook<UserArtPacks, USignWebHookResponse<ResultsHookPostNewArtist>>(
                HOOK_URL_ARTIST,
                dataToSave,
            );
            if (answer?.resultsWebhookSteps?.data?.success) {
                setErrorMsg({
                    severity: 'success',
                    msg: 'Your request to join ArtPacks has been successfully submitted. Please wait for our curatorial team to review it.',
                });
                setShowError(true);
                return true;
            }
            const errorMsg = `Erro @saveData: ${answer?.resultsWebhookSteps?.data?.error} `;
            console.error(errorMsg);
            setErrorMsg({
                severity: 'error',
                msg:
                    answer?.resultsWebhookSteps?.data?.error ??
                    'Your request has failed. Please try again, and if the issue persists, contact ArtPacks through our contact form on our website.',
            });
            setShowError(true);
        } catch (err: unknown) {
            if (err instanceof Error) {
                const errorMsg = `Erro @saveProfile: ${err.message} `;
                console.error(errorMsg);
                setErrorMsg({
                    severity: 'error',
                    msg: 'Your request has failed. Please try again, and if the issue persists, contact ArtPacks through our contact form on our website.',
                });
                setShowError(true);
            } else {
                const errorMsg = 'Erro @saveProfile';
                console.error(errorMsg);
                setErrorMsg({
                    severity: 'error',
                    msg: 'Your request has failed. Please try again, and if the issue persists, contact ArtPacks through our contact form on our website.',
                });
                setShowError(true);
            }
        }
        return false;
    };

    const checkBeforeSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSaving(true);
        if (await saveData()) {
            setNeedSave(false);
        }
        setSaving(false);
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Paper elevation={0} sx={{ display: 'flex', marginBottom: 2 }}>
                <Card
                    sx={{
                        padding: 2,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <EditNoteIcon fontSize="large" />
                </Card>
                <Card sx={{ padding: 1, ml: 1 }} elevation={0}>
                    <Typography variant="h6" component="div">
                        ArtPacks Submission
                    </Typography>
                    <Typography variant="caption" component="div" alignSelf={'baseline'}>
                        {`v.${pkg.version}`}
                    </Typography>
                </Card>
            </Paper>

            <Snackbar
                open={showError}
                autoHideDuration={15000}
                onClose={handleCloseErrorMsg}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseErrorMsg} severity={errorMsg?.severity ?? 'error'} sx={{ width: '100%' }}>
                    {errorMsg.msg}
                </Alert>
            </Snackbar>

            <Box
                component="form"
                sx={{ opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}
                onSubmit={(e) => {
                    checkBeforeSave(e).catch((err: unknown) => {
                        console.error(err);
                    });
                }}>
                <Grid container>
                    <Grid item xs={12}>
                        <FormGroup row sx={{ width: '100%', display: 'flex', mt: 3, gap: 3 }}>
                            <TextField
                                required
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Email"
                                value={formData?.email}
                                onChange={(e) => {
                                    changeFormData({ email: e.target.value });
                                }}
                            />
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel id="salesTier-label">Sales Tier</InputLabel>
                                <Select
                                    labelId="salesTier-label"
                                    id="salesTier"
                                    value={formData?.salesTier}
                                    label="salesTier"
                                    onChange={(e) => {
                                        changeFormData({ salesTier: e.target.value as typeof formData.salesTier });
                                    }}>
                                    <MenuItem value={'Emerging (no sales up to 1.5 ETH total volume)'}>
                                        Emerging (no sales up to 1.5 ETH total volume)
                                    </MenuItem>
                                    <MenuItem value={'Rising (total volume from 1.5 to 15 ETH)'}>
                                        Rising (total volume from 1.5 to 15 ETH)
                                    </MenuItem>
                                    <MenuItem value={'Iconic (total volume from 15 to 50 ETH)'}>
                                        Iconic (total volume from 15 to 50 ETH)
                                    </MenuItem>
                                    <MenuItem value={'Epic (50 to 250 ETH total volume on sales)'}>
                                        Epic (50 to 250 ETH total volume on sales)
                                    </MenuItem>
                                    <MenuItem value={'Legendary (for artists with over 250 ETH on sales)'}>
                                        Legendary (for artists with over 250 ETH on sales)
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                        <FormGroup row sx={{ width: '100%', display: 'flex', mt: 3, gap: 3 }}>
                            <TextField
                                required
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Artist Name"
                                value={formData?.artistName}
                                onChange={(e) => {
                                    changeFormData({ artistName: e.target.value });
                                }}
                            />
                            <TextField
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Residence Country"
                                value={formData?.residenceCountry}
                                onChange={(e) => {
                                    changeFormData({ residenceCountry: e.target.value });
                                }}
                            />
                        </FormGroup>
                        <FormGroup row sx={{ width: '100%', display: 'flex', mt: 3, gap: 3 }}>
                            <TextField
                                required
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="First Name"
                                value={formData?.firstName}
                                onChange={(e) => {
                                    changeFormData({ firstName: e.target.value });
                                }}
                            />
                            <TextField
                                required
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Last Name"
                                value={formData?.lastName}
                                onChange={(e) => {
                                    changeFormData({ lastName: e.target.value });
                                }}
                            />
                        </FormGroup>
                        <FormGroup row sx={{ width: '100%', display: 'flex', mt: 3, gap: 3 }}>
                            <TextField
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Birth Year"
                                value={formData?.birthYear}
                                onChange={(e) => {
                                    changeFormData({ birthYear: e.target.value });
                                }}
                            />
                            <TextField
                                sx={{ flex: 1 }}
                                variant="outlined"
                                label="Birth Country"
                                value={formData?.birthCountry}
                                onChange={(e) => {
                                    changeFormData({ birthCountry: e.target.value });
                                }}
                            />
                        </FormGroup>
                        <TextField
                            sx={{ width: '100%', mt: 3 }}
                            variant="outlined"
                            label="Mint Wallet"
                            value={formData?.mintWallet}
                            onChange={(e) => {
                                changeFormData({ mintWallet: e.target.value });
                            }}
                        />
                        <TextField
                            sx={{ width: '100%', mt: 3 }}
                            variant="outlined"
                            label="Sales Wallet"
                            value={formData?.salesWallet}
                            onChange={(e) => {
                                changeFormData({ salesWallet: e.target.value });
                            }}
                        />
                        <TextField
                            sx={{ width: '100%', mt: 3 }}
                            variant="outlined"
                            label="Portfolio Link"
                            value={formData?.portfolioLink}
                            onChange={(e) => {
                                changeFormData({ portfolioLink: e.target.value });
                            }}
                        />
                        <TextField
                            sx={{ width: '100%', mt: 3 }}
                            variant="outlined"
                            label="Twitter/X"
                            value={formData?.twitterHandle}
                            onChange={(e) => {
                                changeFormData({ twitterHandle: e.target.value });
                            }}
                        />
                        <TextField
                            sx={{ width: '100%', mt: 3 }}
                            variant="outlined"
                            label="Oncyber Gallery"
                            value={formData?.oncyberGallery}
                            onChange={(e) => {
                                changeFormData({ oncyberGallery: e.target.value });
                            }}
                        />
                        <Stack direction="row" spacing={3} sx={{ width: '100%', mt: 3, justifyContent: 'end' }}>
                            <LoadingButton type="submit" loading={saving} variant="contained" disabled={!needSave}>
                                <span>Submit</span>
                            </LoadingButton>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default EditUser;
